import React, { useMemo } from 'react';
import { Stack, Typography } from '@esgian/esgianui';
import { LegendValue, TextWithTooltipIcon } from '@components/Display';
import { adjustColorOpacity } from '@components/Charts/ChartJsColorHelpers';
import PropTypes from 'prop-types';

function CanvasLegends({
  chartRef = undefined,
  id = '',
  data = { datasets: [] },
  onToggle = undefined
}) {
  const handleLegendClick = (label) => {
    // Visibility is owned by the parent (hiddenLabels); toggling triggers a data rebuild that
    // re-applies dataset.hidden, so it survives date changes / refetches.
    onToggle?.(label);
  };
  const handleLegendHover = (label, highlight) => {
    const chart = chartRef.current;
    if (chartRef.current) {
      const indexOfData = chart.data?.datasets.findIndex((d) => (d.label ?? '') === label);
      chart.options.animation.duration = 200; // Set a faster animation duration
      chart.data.datasets.forEach((dataset, index) => {
        if (highlight && index !== indexOfData) {
          dataset.borderColor = adjustColorOpacity(dataset.borderColor, 0.1);
          dataset.backgroundColor = adjustColorOpacity(dataset.backgroundColor, 0.1);
        } else {
          dataset.borderColor = adjustColorOpacity(dataset.borderColor, 1);
          dataset.backgroundColor = dataset.originalBackgroundColor;
        }
      });
      chart.update();
    }
  };

  const renderLegend = (dataset, groupName = null, key) => {
    return (
      <Stack
        direction={'row'}
        spacing={1}
        key={key}
        sx={{ rowGap: 'normal' }}
        useFlexGap
        flexWrap="wrap">
        {groupName && <Typography variant={'caption'}>{groupName}:</Typography>}
        {[...dataset]
          .filter(({ legendHidden }) => !legendHidden)
          .map(
            (
              {
                type,
                label,
                legendTooltip,
                backgroundColor,
                borderColor,
                disableLegend,
                subHeader,
                userHidden,
                subHeaderTooltip
              },
              i
            ) => {
              if (subHeader) {
                if (subHeaderTooltip) {
                  return (
                    <TextWithTooltipIcon
                      key={`${id}-legend-${i}`}
                      iconSize={'16px'}
                      gap={'4px'}
                      label={
                        <Typography key={`${id}-legend-${i}`} variant={'body2'}>
                          {subHeader}
                        </Typography>
                      }
                      tooltipText={subHeaderTooltip}
                    />
                  );
                } else {
                  return (
                    <Typography key={`${id}-legend-${i}`} variant={'body2'}>
                      {subHeader}
                    </Typography>
                  );
                }
              }
              if (legendTooltip && !disableLegend) {
                return (
                  <TextWithTooltipIcon
                    key={`${id}-legend-${i}`}
                    iconSize={'16px'}
                    gap={'4px'}
                    label={
                      <LegendValue
                        handleHover={(highlight) => {
                          handleLegendHover(label, highlight);
                        }}
                        inactive={userHidden}
                        onClick={() => handleLegendClick(label)}
                        value={label}
                        color={type === 'bar' ? backgroundColor : borderColor}
                      />
                    }
                    tooltipText={legendTooltip}
                  />
                );
              }
              return !disableLegend ? (
                <LegendValue
                  handleHover={(highlight) => {
                    handleLegendHover(label, highlight);
                  }}
                  inactive={userHidden}
                  onClick={() => handleLegendClick(label)}
                  key={`${id}-legend-${i}`}
                  value={label}
                  color={type === 'bar' ? backgroundColor : borderColor}
                />
              ) : null;
            }
          )}
      </Stack>
    );
  };

  const legendGroups = useMemo(() => {
    let groupList = [];
    data.datasets.forEach(({ group }) => {
      if (!!group && !groupList.includes(group)) {
        groupList.push(group);
      }
    });
    return groupList;
  }, [data]);

  if (legendGroups.length) {
    return (
      <Stack spacing={1}>
        {legendGroups.map((groupName, i) => {
          let currSet = [...data.datasets].filter(({ group }) => group === groupName);
          return renderLegend(currSet, groupName, `legend-${i}`);
        })}
      </Stack>
    );
  }
  return renderLegend(data.datasets);
}

CanvasLegends.propTypes = {
  chartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  id: PropTypes.string,
  renderGroups: PropTypes.bool,
  data: PropTypes.object,
  onToggle: PropTypes.func
};

export default CanvasLegends;
